import { Injectable, computed, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../environments/environment';
import { AuthStateService } from './auth-state.service';

export const UserRoleValue = {
  Base: 'BASE',
  Standard: 'STANDARD',
  Referente: 'REFERENTE',
  Admin: 'ADMIN',
} as const;

export type UserRole = (typeof UserRoleValue)[keyof typeof UserRoleValue] | null;

/**
 * Unica fonte di verità per il ruolo dell'utente autenticato.
 *
 * Estrae il ruolo dal claim custom Auth0 (`environment.auth.roleClaim`) non appena
 * `auth.user$` emette un profilo, lo normalizza e lo espone come signal reattivo.
 * Nessun altro componente/servizio deve leggere il claim o normalizzare il ruolo:
 * devono limitarsi a consumare questo servizio.
 */
@Injectable({
  providedIn: 'root',
})
export class UserRoleService {
  private readonly authState = inject(AuthStateService);
  private readonly logger = inject(NGXLogger);

  readonly role = computed<UserRole>(() => {
    const isAuthenticated = this.authState.isAuthenticated();
    const user = this.authState.user();

    if (!isAuthenticated || !user) {
      return null;
    }

    const roles = user[environment.auth.roleClaim] as string[] | undefined;
    const rawRole = Array.isArray(roles) && roles.length > 0 ? roles[0] : null;

    return rawRole ? this.normalizeRole(rawRole) : UserRoleValue.Base;
  });

  readonly isViewer = computed(() => this.role() === UserRoleValue.Base || this.role() === UserRoleValue.Standard);

  /** L'utente può modificare (cioè non è VIEWER). */
  // Prima era !this.isViewer()
  readonly canModify = computed(() => this.role() === UserRoleValue.Referente || this.role() === UserRoleValue.Admin);

  private normalizeRole(role: string): UserRole {
    const normalized = role.toUpperCase().replace(/[\s_]+/g, '');
    switch (normalized) {
      case 'ADMIN':
        return UserRoleValue.Admin;
      case 'STANDARD':
        return UserRoleValue.Standard;
      case 'REFERENTE':
        return UserRoleValue.Referente;
      default:
        this.logger.warn('[UserRoleService] Ruolo non riconosciuto:', role);
        return UserRoleValue.Base; // Default to BASE if role is unrecognized
    }
  }

}
